import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { OrderItemResponse, OrderResponse } from '@/types/order-type';
import { StoreSetting } from '@/types/setting-type';
import { format } from 'date-fns';
import { Printer } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { ReceiptPreviewCard } from './receipt';
import { Customer } from '@/types/customer-type';
type Money = number;

type InvoiceProps = {
  company: StoreSetting;
  customer: Customer;
  order: OrderResponse;
  items: OrderItemResponse[];
  deriveTotals?: boolean;
  autoPrint?: boolean;
  onDownloadPdf?: () => void; // optional, fallback to window.print()
};

const currency = (n: number, cur = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

// ---- Invoice (printable) ----------------------------------------------------
export const Invoice = forwardRef<HTMLDivElement, InvoiceProps>(function Invoice(
  { company, customer, order, items, deriveTotals = true, onDownloadPdf },
  ref,
) {
  const currencyCode = 'USD'; //todo

  const computed = useMemo(() => {
    const lineSubtotals = items.map(
      (it) => it.quantity * (it.price ?? 0) - (it.discount ?? 0),
    );
    const subtotal = sum(lineSubtotals);
    const discountOrder = order.discountTotal ?? 0;
    const tax = (order.taxRate ?? 0) * Math.max(0, subtotal - discountOrder);
    const shipping = order.shippingFee ?? 0;
    const total = Math.max(0, subtotal - discountOrder) + tax + shipping;
    return { subtotal, discountOrder, tax, shipping, total };
  }, [items, order.discountTotal, order.shippingFee, order.taxRate]);

  const totals = deriveTotals
    ? computed
    : { subtotal: 0, discountOrder: 0, tax: 0, shipping: 0, total: 0 }; // or use props if you add them

  const invoiceNo = order.orderNumber ?? `INV-${order.id}`;
  const createdAt =
    typeof order.createdAt === 'string' ? new Date(order.createdAt) : order.createdAt;

  return (
    <>
      {/* Print styles (A4, hide buttons when printing) */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 16mm; }
          html, body { background: #fff !important; }
        }
      `}</style>

      <div
        ref={ref ?? undefined}
        className="invoice-container mx-auto max-w-4xl bg-white rounded-lg border shadow-sm print:shadow-none print:border-0"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6">
          <div className="flex items-center gap-4">
            {company.logo ? (
              <img src={company.logo} alt={company.name} className="h-12 w-12 object-contain" />
            ) : (
              <div className="h-12 w-12 rounded bg-slate-100 grid place-items-center text-slate-500 font-semibold">
                {company.name?.[0] ?? 'C'}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold">{company.name}</h1>
              <div className="text-xs text-slate-600">
                {company.addressLine1 && <div>{company.addressLine1}</div>}
                {company.addressLine2 && <div>{company.addressLine2}</div>}
                {(company.phone || company.email) && (
                  <div>
                    {company.phone && <span>Tel: {company.phone}</span>}
                    {company.phone && company.email && <span> • </span>}
                    {company.email && <span>{company.email}</span>}
                  </div>
                )}
                {company.website && <div>{company.website}</div>}
              </div>
            </div>
          </div>

          <div className="text-right space-y-1">
            <div className="text-2xl font-bold">INVOICE</div>
            <div className="text-sm text-slate-600">
              <div>
                <span className="font-semibold">Invoice #:</span> {invoiceNo}
              </div>
              <div>
                <span className="font-semibold">Date:</span>{' '}
                {createdAt && format(createdAt, 'yyyy-MM-dd')}
              </div>
              {order.status && (
                <div>
                  <span className="font-semibold">Status:</span> {order.status}
                </div>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Bill To / Ship To */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div>
            <div className="text-sm font-semibold">Bill To</div>
            <div className="mt-2 text-sm">
              <div className="font-semibold">{customer.name}</div>
              {customer.address && <div>{customer.address}</div>}
              {customer.address2 && <div>{customer.address2}</div>}
              {(customer.phone || customer.email) && (
                <div className="text-slate-600">
                  {customer.phone && <div>Phone: {customer.phone}</div>}
                  {/* {customer.email && <div>Email: {customer.email}</div>} */}
                </div>
              )}
              <div className="text-slate-500 text-xs mt-1">Customer ID: {customer.id}</div>
            </div>
          </div>

          <div className="md:text-right">
            <div className="text-sm font-semibold">Invoice Info</div>
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              {order.paymentMethod && <div>Payment Method: {order.paymentMethod}</div>}
              {order.trackingNumber && <div>Tracking #: {order.trackingNumber}</div>}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="px-6 pb-6">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-4 py-2">Item</th>
                  <th className="px-4 py-2 hidden sm:table-cell">SKU</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Unit Price</th>
                  {/* <th className="px-4 py-2 text-right hidden sm:table-cell">
                      Line Discount
                    </th> */}
                  <th className="px-4 py-2 text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const lineSubtotal = it.quantity * (it.price || 0) - (it.discount ?? 0);
                  return (
                    <tr key={it.id} className="border-t">
                      <td className="px-4 py-2">
                        <div className="font-medium">{it.productName}</div>
                      </td>
                      <td className="px-4 py-2 hidden sm:table-cell text-slate-600">
                        {it.sku ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right">{it.quantity}</td>
                      <td className="px-4 py-2 text-right">
                        {currency((it.price || 0), currencyCode)}
                      </td>
                      {/* <td className="px-4 py-2 text-right hidden sm:table-cell">
                          {it.discount
                            ? `- ${currency(it.discount, currencyCode)}`
                            : "—"}
                        </td> */}
                      <td className="px-4 py-2 text-right font-semibold">
                        {currency(lineSubtotal, currencyCode)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mt-6">
            <div className="text-xs text-slate-600 max-w-md">
              <div className="font-semibold text-slate-800 mb-1">Notes</div>
              <div>{order.notes ?? 'Thank you for your purchase!'}</div>
              <div className="mt-2 text-[11px]">
                * Goods sold are not returnable unless defective. Please contact support within 7
                days.
              </div>
            </div>

            <div className="w-full sm:w-80">
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <div className="text-slate-600">Subtotal</div>
                <div className="text-right">{currency(totals.subtotal, currencyCode)}</div>

                <div className="text-slate-600">Order Discount</div>
                <div className="text-right">
                  {totals.discountOrder ? `- ${currency(totals.discountOrder, currencyCode)}` : '—'}
                </div>

                <div className="text-slate-600">Shipping</div>
                <div className="text-right">
                  {totals.shipping ? currency(totals.shipping, currencyCode) : '—'}
                </div>

                <div className="text-slate-600">
                  Tax {order.taxRate ? `(${(order.taxRate * 100).toFixed(0)}%)` : ''}
                </div>
                <div className="text-right">
                  {totals.tax ? currency(totals.tax, currencyCode) : '—'}
                </div>
              </div>

              <Separator className="my-2" />

              <div className="flex items-center justify-between text-base font-bold">
                <div>Total</div>
                <div>{currency(totals.total, currencyCode)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// ---- Wrapper with buttons (print / download) --------------------------------
export default function InvoicePreviewCard(props: InvoiceProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [showReceipt, setShowingReceipt] = useState<boolean>(false);
  const { company, customer, order, items, autoPrint } = props;

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=1000');
    if (!printWindow) {
      toast({ title: 'Error', description: 'Please allow popups to print' });
      return;
    }

    // Get the invoice HTML
    const invoiceContent = printRef.current?.innerHTML || '';

    // Write the HTML to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${order.orderNumber ?? order.id}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              background: white;
              padding: 0;
              margin: 0;
            }

            @page {
              size: A4;
              margin: 16mm;
            }

            .invoice-container {
              max-width: 100%;
              background: white;
              border: none;
              border-radius: 0;
              box-shadow: none;
            }

            .flex {
              display: flex;
            }

            .items-start {
              align-items: flex-start;
            }

            .items-center {
              align-items: center;
            }

            .justify-between {
              justify-content: space-between;
            }

            .gap-4 {
              gap: 1rem;
            }

            .gap-6 {
              gap: 1.5rem;
            }

            .p-6 {
              padding: 1.5rem;
            }

            .px-4 {
              padding-left: 1rem;
              padding-right: 1rem;
            }

            .py-2 {
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
            }

            .px-6 {
              padding-left: 1.5rem;
              padding-right: 1.5rem;
            }

            .pb-6 {
              padding-bottom: 1.5rem;
            }

            .text-right {
              text-align: right;
            }

            .text-center {
              text-align: center;
            }

            .text-xl {
              font-size: 1.25rem;
              line-height: 1.75rem;
            }

            .text-2xl {
              font-size: 1.5rem;
              line-height: 2rem;
            }

            .text-xs {
              font-size: 0.75rem;
              line-height: 1rem;
            }

            .text-sm {
              font-size: 0.875rem;
              line-height: 1.25rem;
            }

            .text-base {
              font-size: 1rem;
              line-height: 1.5rem;
            }

            .font-bold {
              font-weight: 700;
            }

            .font-semibold {
              font-weight: 600;
            }

            .font-medium {
              font-weight: 500;
            }

            .text-slate-600 {
              color: #475569;
            }

            .text-slate-700 {
              color: #334155;
            }

            .text-slate-800 {
              color: #1e293b;
            }

            .text-slate-500 {
              color: #64748b;
            }

            .bg-slate-50 {
              background-color: #f8fafc;
            }

            .bg-slate-100 {
              background-color: #f1f5f9;
            }

            .space-y-1 > * + * {
              margin-top: 0.25rem;
            }

            .mt-1 {
              margin-top: 0.25rem;
            }

            .mt-2 {
              margin-top: 0.5rem;
            }

            .mt-6 {
              margin-top: 1.5rem;
            }

            .mb-1 {
              margin-bottom: 0.25rem;
            }

            .my-2 {
              margin-top: 0.5rem;
              margin-bottom: 0.5rem;
            }

            .grid {
              display: grid;
            }

            .grid-cols-1 {
              grid-template-columns: repeat(1, minmax(0, 1fr));
            }

            .grid-cols-2 {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .gap-y-1 {
              row-gap: 0.25rem;
            }

            .overflow-x-auto {
              overflow-x: auto;
            }

            .rounded-md {
              border-radius: 0.375rem;
            }

            .rounded {
              border-radius: 0.25rem;
            }

            .border {
              border: 1px solid #e2e8f0;
            }

            .border-t {
              border-top: 1px solid #e2e8f0;
            }

            hr {
              border: none;
              border-top: 1px solid #e2e8f0;
              margin: 0.5rem 0;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th {
              text-align: left;
              padding: 0.5rem 1rem;
            }

            td {
              padding: 0.5rem 1rem;
            }

            .w-full {
              width: 100%;
            }

            .max-w-md {
              max-width: 28rem;
            }

            .h-12 {
              height: 3rem;
            }

            .w-12 {
              width: 3rem;
            }

            .object-contain {
              object-fit: contain;
            }

            .grid.place-items-center {
              place-items: center;
            }

            @media (min-width: 768px) {
              .md\\:grid-cols-2 {
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }

              .md\\:text-right {
                text-align: right;
              }
            }

            @media (min-width: 640px) {
              .sm\\:w-80 {
                width: 20rem;
              }

              .sm\\:flex-row {
                flex-direction: row;
              }

              .sm\\:items-end {
                align-items: flex-end;
              }

              .sm\\:justify-between {
                justify-content: space-between;
              }

              .sm\\:table-cell {
                display: table-cell;
              }
            }

            .hidden {
              display: none;
            }

            @media (min-width: 640px) {
              .sm\\:table-cell {
                display: table-cell;
              }
            }
          </style>
        </head>
        <body>
          ${invoiceContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  useEffect(() => {
    if (autoPrint) {
      handlePrint();
    }
  }, [autoPrint]);

  return (
    <Card>
      <CardHeader className="pb-2 flex items-center justify-between">
        <CardTitle className="text-sm font-medium">Invoice Preview</CardTitle>
        <div className="no-print flex gap-2">
          {!showReceipt && (
            <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
              <Printer className="h-4 w-4" />
              Print / PDF
            </Button>
          )}
          {/* <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowingReceipt(!showReceipt)}>
                        <ReceiptIcon className="h-4 w-4" />
                        {showReceipt ? 'Hide Receipt' : 'Show Receipt'}
                    </Button> */}
        </div>
      </CardHeader>
      <CardContent className="max-w-3xl mx-auto">
        {!showReceipt && <Invoice ref={printRef} {...props} />}
        {showReceipt && (
          <ReceiptPreviewCard
            orderId={order.id}
          />
        )}
      </CardContent>
    </Card>
  );
}
